import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';

/* eslint-disable no-param-reassign */
/* eslint-disable brace-style */
/**
 * Namespace for the Sparta Shield Utility.
 * @constant {string}
 */
const utilityNamespace = 'spartaCarouselUtility';

/**
* Collection of namespaced error strings.
* @constant {object}
*/
const ERROR = {
  CAROUSEL_ITEM_LENGTH_ERROR: `${utilityNamespace}: At least one slide is required to render the carousel`
};

/**
* Collection of namespaced default settings
* @constant {object}
*/
const DEFAULT_SETTINGS = {
  CAROUSEL_ITEM: 'carousel__item',
  CAROUSEL_NAV_CLASSES: 'carousel__nav',
  CAROUSEL_DOT: 'carousel__dot',
  ARROW: 'arrow',
  ARROW_NEXT: 'arrow--next',
  ARROW_PREV: 'arrow--prev',
  NEXT: 'next',
  PREV: 'prev',
  ARROW_ACTIVE: 'arrow-active',
  CAROUSEL_ITEM__SELECTED: 'carousel__item--selected',
  CAROUSEL_DOT__SELECTED: 'carousel__dot--selected',
  CAROUSEL: 'spa-carousel',
  ARROW_DISABLED: 'carousel__arrow--disabled',
  CAROUSEL_SLIDES: 'carousel__slides',
  CAROUSEL_ANIMATION__DISABLE: 'carousel-animation--disable',
  OFFSET_CLASSES: {
    OFFSET: 'carousel__dot--offset',
    DOT_HIDE: 'carousel__dot--hide'
  }
};

// Create localized instance of settings
const instance = {};

/**
 * Check if carousel items are the valid length (2-7 slides) and if so create the carousel and dot controls
 */
function createCarousels(defaultSettings) {
  // Pull in spa params
  const lang = spartaGlobalFunctions.getLanguage();
  const spaParam = spartaGlobalFunctions.getSpaParams();
  const carouselsGlobalParams = spaParam.global[lang].carousels;
  Object.assign(instance, {
    prevArrow: `<button class="spa-icon arrow arrow--prev" aria-label="${carouselsGlobalParams.ariaLabelPrev}"></button>`,
    nextArrow: `<button class="spa-icon arrow arrow--next" aria-label="${carouselsGlobalParams.ariaLabelNext}"></button>`,
    carousels: [...document.getElementsByClassName(DEFAULT_SETTINGS.CAROUSEL)],
    ariaLabel: carouselsGlobalParams.ariaLabel
  });
  instance.carousels.forEach(carousel => {
    instance.items = carousel.getElementsByClassName(DEFAULT_SETTINGS.CAROUSEL_ITEM);
    if (instance.items.length === 0) {
      console.log(ERROR.CAROUSEL_ITEM_LENGTH_ERROR);
    } else if (document.querySelectorAll(`[id='${carousel.id}'] .${DEFAULT_SETTINGS.CAROUSEL_NAV_CLASSES}`).length === 0) {
      createDots(carousel);
    }
  });
}

/**
* Create dots builds the html for the dots and arrows and adds it to the DOM prior to adding logic to arrows
* @param {object} carousel - the individual carousel being looped through 
* @param {object} DEFAULT_SETTINGS - default settings for carousel to be used across functions
*/
function createDots(carousel) {
  const dotsHtml = Array.from(instance.items, () => {
    return `<span class='${DEFAULT_SETTINGS.CAROUSEL_DOT}'></span>`;
  });
  carousel.insertAdjacentHTML('beforeend', `
    <div class='${DEFAULT_SETTINGS.CAROUSEL_NAV_CLASSES} ${instance.items.length === 1 ? 'spa-hidden' : ''}'>
      ${instance.prevArrow}
      <div class="carousel__dot--wrapper">
        ${dotsHtml.join('')}
      </div>
      ${instance.nextArrow}
    </div>
  `);

  // add arrow logic
  arrowLogicHandling(carousel);
}

/**
* Arrow Logic Handling handles binding event listeners to carousel prev/next arrow
* and handles the logic between which slides/dots are shown/hidden as well as their css styling
* @param {object} carousel - the individual carousel being looped through 
* @param {object} DEFAULT_SETTINGS - default settings for carousel to be used across functions
*/
function arrowLogicHandling(carousel) {
  const dots = [...carousel.getElementsByClassName(DEFAULT_SETTINGS.CAROUSEL_DOT)];
  const arrows = [...carousel.getElementsByClassName(DEFAULT_SETTINGS.ARROW)];
  const slides = [...carousel.getElementsByClassName(DEFAULT_SETTINGS.CAROUSEL_ITEM)];
  const slideWrapper = carousel.querySelector(`.${DEFAULT_SETTINGS.CAROUSEL_SLIDES}`);
  const carouselID = carousel.id;
  let slideShowing = 0;

  // add event handlers for arrows
  arrows.forEach(arrow => {
    arrow.addEventListener('click', e => {
      const targetArrowClicked = e.target;
      const previousArrow = targetArrowClicked.classList.contains(DEFAULT_SETTINGS.ARROW_PREV);
      const arrowClicked = previousArrow ? 'prev' : 'next';
      animateCarousel({
        targetArrowClicked,
        carouselID,
        arrowClicked
      });
    });
  });

  // Select the first item on page load
  instance.items[slideShowing].classList.add(DEFAULT_SETTINGS.CAROUSEL_ITEM__SELECTED);
  dots[slideShowing].classList.add(DEFAULT_SETTINGS.CAROUSEL_DOT__SELECTED);

  // add event handlers for slides
  slides.forEach(slide => {
    addGestures(slide);
  });

  // add tab index on slides not showing
  addTabIndexForSlidesNotShowing(carouselID);

  // determine how many items are in a carousel, if more than 5, hide the last few dots on page load
  if (dots.length > 5) {
    dots.forEach((dot, index) => {
      if (index === 0) {
        dots[0].classList.add(DEFAULT_SETTINGS.CAROUSEL_DOT__SELECTED);
      } else {
        dots[index].classList.add(`${DEFAULT_SETTINGS.OFFSET_CLASSES.OFFSET}-${index.toString()}`);
      }
      if (index !== undefined && index >= 5) {
        dots[index].classList.add(DEFAULT_SETTINGS.OFFSET_CLASSES.DOT_HIDE);
      }
    });
  }

  // add ADA labels
  updateSlideXofYLabel({
    carousel,
    slideShowing,
    slides
  });

  /**
   * Adds an animation for the carousel slides and handles the incrementing/decrementing logic of the carousel controls
   * @param {string} carouselID - id of the carousel parent wrapper
   * @param {string} arrowClicked - prev/next string value
   */
  function animateCarousel(settings) {
    const {
      carouselID,
      arrowClicked
    } = {
      ...settings
    };
    const allSlides = document.querySelectorAll(`[id='${carouselID}'] .${DEFAULT_SETTINGS.CAROUSEL_ITEM}`);

    // remove css classes in order to loop over and reapply classes
    allSlides.forEach(item => item.classList.remove(DEFAULT_SETTINGS.CAROUSEL_ITEM__SELECTED));
    arrows.forEach(arrow => arrow.classList.remove(DEFAULT_SETTINGS.ARROW_ACTIVE));
    dots.forEach(dot => {
      dot.classList.forEach(className => {
        dot.classList.remove(DEFAULT_SETTINGS.CAROUSEL_DOT__SELECTED, DEFAULT_SETTINGS.OFFSET_CLASSES.DOT_HIDE);
        if (className.startsWith(DEFAULT_SETTINGS.OFFSET_CLASSES.OFFSET)) {
          for (let i = 0; i < 5; i++) {
            dot.classList.remove(`${DEFAULT_SETTINGS.OFFSET_CLASSES.OFFSET}-${(i + 1).toString()}`);
          }
        }
      });
    });

    // Get computed CSS value
    let computedStyle = slideWrapper.style.transform;
    computedStyle = computedStyle.replace(/[^-?\d.]/g, '');
    const currentPosition = Number(computedStyle || slideWrapper.scrollLeft);
    const wrapperWidth = slideWrapper.getBoundingClientRect().width;
    let newPosition;
    if (arrowClicked === 'prev') {
      // handle previous click
      if (slideShowing === 0) {
        // if on the first slide, move to the last slide when previous arrow clicked
        slideShowing = allSlides.length - 1;
        newPosition = wrapperWidth * -(allSlides.length - 1);
      } else {
        // otherwise, decrement by 1
        slideShowing = slideShowing - 1;
        newPosition = currentPosition + wrapperWidth;
      }
    } else {
      // handle next click
      if (slideShowing === allSlides.length - 1) {
        // if on the last slide, move to the first slide when next arrow is clicked
        slideShowing = 0;
        newPosition = 0;
      } else {
        slideShowing = slideShowing + 1;
        newPosition = currentPosition - wrapperWidth;
      }
    }
    slideWrapper.style.transform = `translateX(${newPosition}px)`;
    allSlides[slideShowing].classList.add(DEFAULT_SETTINGS.CAROUSEL_ITEM__SELECTED);
    dots[slideShowing].classList.add(DEFAULT_SETTINGS.CAROUSEL_DOT__SELECTED);
    addTabIndexForSlidesNotShowing(carouselID);

    // if dots are more than 5, add offset and hide css classes
    if (dots.length > 5) {
      addOffsetClasses(dots, slideShowing);
      addHideClasses(dots, slideShowing);
    }
    updateSlideXofYLabel({
      carousel,
      slideShowing,
      slides
    });
  }

  /**
   * addGestures applies event listeners to detect when users touch/interact with a carousel slide, calculating which direction the user is swipe gesturing in
   * @param {object} slide - the individual slide being looped through
   */
  function addGestures(slide) {
    let touchstartX = 0;
    let touchendX = 0;
    let fingersTouchingScreen = 0;
    const minimumSwipeGestureToRegisterSwipeInPixels = 60;
    slide.addEventListener('touchstart', function (event) {
      touchstartX = event.changedTouches[0].screenX;
      event.changedTouches[0].screenY;
      fingersTouchingScreen = event.touches.length;
    }, false);
    slide.addEventListener('touchend', function (event) {
      touchendX = event.changedTouches[0].screenX;
      event.changedTouches[0].screenY;
      handleGesture();
    }, false);
    function handleGesture() {
      if (fingersTouchingScreen === 1) {
        const xChange = touchendX - touchstartX;
        if (touchendX < touchstartX && Math.abs(xChange) > minimumSwipeGestureToRegisterSwipeInPixels) {
          const targetArrowClicked = document.querySelector(`[id='${carouselID}'] .${DEFAULT_SETTINGS.ARROW_NEXT}`);
          const arrowClicked = DEFAULT_SETTINGS.NEXT;
          animateCarousel({
            targetArrowClicked,
            carouselID,
            arrowClicked
          });
        }
        if (touchendX > touchstartX && Math.abs(xChange) > minimumSwipeGestureToRegisterSwipeInPixels) {
          const targetArrowClicked = document.querySelector(`[id='${carouselID}'] .${DEFAULT_SETTINGS.ARROW_PREV}`);
          const arrowClicked = DEFAULT_SETTINGS.PREV;
          animateCarousel({
            targetArrowClicked,
            carouselID,
            arrowClicked
          });
        }
      }
    }
  }
}

/**
 * loopOverChildElements sets tab index to elements in slides to allow only elements on the slide showing to be able to be tabbed to
 * @param {object} settings - holds settings variables used to loop over carousel slide child elements to determine if they should be tabbable to when slide is not active 
 */
function loopOverChildElements(settings) {
  const {
    slide,
    option
  } = settings;
  // array of elements that are tabbable
  const tabbable = ['a', 'input', 'select', 'button', 'textarea', 'iframe'];
  // loop over tabbable elements within each individual carousel to set tabindex value if the slide is showing or not
  tabbable.forEach(element => {
    const childElem = slide.querySelectorAll(element);
    if (childElem.length > 0 && childElem[0] !== undefined) {
      if (option === 'setTabIndex') {
        childElem.forEach(el => {
          el.setAttribute('tabindex', -1);
        });
      } else if (option === 'removeTabIndex') {
        childElem.forEach(el => {
          el.removeAttribute('tabindex');
        });
      }
    }
  });
}

/**
 * 
 * @param {string} carouselID - The ID of the carousel we are targeting to loop over 
 */
function addTabIndexForSlidesNotShowing(carouselID) {
  const allSlides = document.querySelectorAll(`[id='${carouselID}'] .${DEFAULT_SETTINGS.CAROUSEL_ITEM}`);
  allSlides.forEach(slide => {
    if (!slide.classList.contains(DEFAULT_SETTINGS.CAROUSEL_ITEM__SELECTED)) {
      const option = 'setTabIndex';
      slide.setAttribute('aria-hidden', 'true');
      loopOverChildElements({
        slide,
        option
      });
    } else {
      const option = 'removeTabIndex';
      slide.removeAttribute('aria-hidden');
      loopOverChildElements({
        slide,
        option
      });
    }
  });
}

/**
 * update ADA value on slides (Slide X of Y)
 * @param {object} settings - Settings object passing carousel data
 */
function updateSlideXofYLabel(settings) {
  const {
    carousel,
    slideShowing,
    slides
  } = {
    ...settings
  };
  const ariaLive = carousel.querySelector(`.spa-aria-live`);
  const slideAdaDescription = slides[slideShowing].getAttribute('data-ada');
  const updatedAiraLabel = `
    ${instance.ariaLabel.replace('[slideIndex]', `${slideShowing + 1}`).replace('[slidesLength]', slides.length)}
    ${slideAdaDescription ? ` - ${slideAdaDescription}` : ''}
  `.trim();
  ariaLive.innerHTML = updatedAiraLabel;
}

/**
 * Get the active slide
 * @param {object} slides is the html object of the slides
 * @returns
 */
function getActiveSlide(slides) {
  return slides.find(o => {
    if (o.classList.contains(DEFAULT_SETTINGS.CAROUSEL_ITEM__SELECTED)) {
      return o;
    }
  });
}

/**
* Add hide classes for dots
* @param {array} dots - dots (dots) 
* @param {number} slideShowing - slideShowing is the current active slide
*/
function addHideClasses(dots, slideShowing) {
  dots.forEach(dot => {
    if (dots.length > 5) {
      const startHighDotHidden = Math.max(5, slideShowing + 3);
      for (let i = startHighDotHidden; i < dots.length; i++) {
        dots[i].classList.add(DEFAULT_SETTINGS.OFFSET_CLASSES.DOT_HIDE);
      }
      const endStartDotsHidden = Math.min(slideShowing - 3, dots.length - 6 /* off-by-one error with 0-index counting */);
      for (let i = 0; i <= endStartDotsHidden; i++) {
        dots[i].classList.add(DEFAULT_SETTINGS.OFFSET_CLASSES.DOT_HIDE);
      }
    }
  });
}

/**
* Add offset classes for dots
* @param {array} dots - dots we are adding offset css classes to add styling
* @param {number} slideShowing - slideShowing is the current active slide
*/
function addOffsetClasses(dots, slideShowing) {
  // checking each slide if there is a dom element before/after the current selected slide/dot
  // if it exists, we apply offset classes for the deminishing dot effect
  for (let i = 0; i < 5; i++) {
    const count = i + 1;
    if (dots[slideShowing - count] !== undefined) {
      dots[slideShowing - count].classList.add(`${DEFAULT_SETTINGS.OFFSET_CLASSES.OFFSET}-${count}`);
    }
    if (dots[slideShowing + count] !== undefined) {
      dots[slideShowing + count].classList.add(`${DEFAULT_SETTINGS.OFFSET_CLASSES.OFFSET}-${count}`);
    }
  }
}

/**
 * resizedWindow fires after the window has finished resizing so the carousel slides can update their position based on the new viewport size
 */
function resizedWindow() {
  if (!instance) {
    return false;
  }
  instance.carousels.forEach(carousel => {
    const thisSlides = [...carousel.getElementsByClassName(DEFAULT_SETTINGS.CAROUSEL_ITEM)];

    // Get active slide
    const activeSlide = getActiveSlide(thisSlides);
    const activeSlideIndex = Array.from(thisSlides).indexOf(activeSlide);
    const thisSlideWrapper = carousel.querySelector(`.${DEFAULT_SETTINGS.CAROUSEL_SLIDES}`);
    // Add temp class to override transition
    thisSlideWrapper.classList.add(DEFAULT_SETTINGS.CAROUSEL_ANIMATION__DISABLE);

    // Get computed CSS value
    let computedStyle = thisSlideWrapper.style.transform;
    computedStyle = computedStyle.replace(/[^-?\d.]/g, '');
    // Take slide index multiplied by current slider width and set to a negative
    let currentPosition = thisSlideWrapper.getBoundingClientRect().width;
    requestAnimationFrame(function () {
      // Update current position value after CSS is applied to
      currentPosition = thisSlideWrapper.getBoundingClientRect().width;
      const newPosition = -(currentPosition * activeSlideIndex);
      thisSlideWrapper.style.transform = `translateX(${newPosition}px)`;
    });

    // Remove transition override class
    clearTimeout(window.carouselResizedFinished);
    window.carouselResizedFinished = setTimeout(function () {
      Array.from(document.getElementsByClassName(DEFAULT_SETTINGS.CAROUSEL_SLIDES)).forEach(el => el.classList.remove(DEFAULT_SETTINGS.CAROUSEL_ANIMATION__DISABLE));
    }, 500);
  });
}

// Resize slider items on window resize - after window has finished resizing, clear the timeout and call the resizedWindow function to reposition slides
let windowHasBeenResized;
window.onresize = () => {
  clearTimeout(windowHasBeenResized);
  windowHasBeenResized = setTimeout(resizedWindow, 100);
};

/**
 * Configures inputs for validation, formatting, obfuscation, etc.
 */
class SpartaCarouselUtility {
  /**
   * Initializes a new instance of the class.
   * @param {object} options - Default options to be used by the utility
   * @returns {void}
   */
  init(options = {}) {
    createCarousels();
  } // END init
}

// Export new instance of the Sparta Tab Utility.
var index = new SpartaCarouselUtility();

export { index as default };
